/**
 * Mascaras de campo de texto curto (campos complementares).
 *
 * A mascara e um PADRAO com marcadores, nao uma expressao regular:
 *
 *   `0` -> um digito (0-9)
 *   `A` -> uma letra (acentos incluidos), normalizada para MAIUSCULA
 *   `a` -> uma letra, normalizada para minuscula
 *   `S` -> letra ou digito, como o usuario digitou
 *   `X` -> letra ou digito, normalizado para MAIUSCULA
 *   `\` -> escapa o proximo caractere (para usar um marcador como literal)
 *
 * Qualquer outro caractere e literal e o motor o insere sozinho enquanto a
 * pessoa digita (pontos do CPF, barra do CNPJ, parenteses do telefone).
 *
 * Uma mascara pode ter VARIOS padroes (`cpf_cnpj`, telefone fixo x celular).
 * Nesse caso vale o primeiro padrao que ainda cabe o que foi digitado — e o
 * ultimo passa a ser o teto, o que impede digitar alem do maior formato.
 */

/** Marcadores que consomem um caractere digitado. */
const MARCADORES = new Set(['0', 'A', 'a', 'S', 'X'])

export type MascaraPronta = {
  /** Chave gravada em `config.mask`. */
  key: string
  /** Rotulo para a tela de administracao. */
  label: string
  /** Um ou mais padroes; varios = formato que cresce conforme digita. */
  patterns: string[]
  /** Teclado sugerido no celular. */
  inputMode?: 'numeric' | 'text' | 'tel'
  /** Exemplo mostrado como placeholder do campo. */
  exemplo: string
}

/**
 * Mascaras que ja vem prontas. A ordem e a que aparece na lista da tela de
 * administracao: as de documento primeiro, que sao as mais pedidas.
 */
export const MASCARAS_PRONTAS: MascaraPronta[] = [
  { key: 'cpf', label: 'CPF', patterns: ['000.000.000-00'], inputMode: 'numeric', exemplo: '123.456.789-00' },
  { key: 'cnpj', label: 'CNPJ', patterns: ['00.000.000/0000-00'], inputMode: 'numeric', exemplo: '12.345.678/0001-90' },
  {
    key: 'cpf_cnpj',
    label: 'CPF ou CNPJ',
    patterns: ['000.000.000-00', '00.000.000/0000-00'],
    inputMode: 'numeric',
    exemplo: '123.456.789-00',
  },
  { key: 'cep', label: 'CEP', patterns: ['00000-000'], inputMode: 'numeric', exemplo: '70000-000' },
  {
    key: 'telefone',
    label: 'Telefone (fixo ou celular)',
    patterns: ['(00) 0000-0000', '(00) 00000-0000'],
    inputMode: 'tel',
    exemplo: '(61) 99999-0000',
  },
  { key: 'data', label: 'Data (dd/mm/aaaa)', patterns: ['00/00/0000'], inputMode: 'numeric', exemplo: '31/12/2026' },
  { key: 'hora', label: 'Hora (hh:mm)', patterns: ['00:00'], inputMode: 'numeric', exemplo: '14:30' },
  {
    key: 'placa',
    label: 'Placa de veículo',
    // o 5o caractere e letra na Mercosul e digito no formato antigo: `X` aceita os dois
    patterns: ['AAA0X00'],
    inputMode: 'text',
    exemplo: 'BRA2E19',
  },
  {
    key: 'cartao',
    label: 'Cartão (16 dígitos)',
    patterns: ['0000 0000 0000 0000'],
    inputMode: 'numeric',
    exemplo: '4111 1111 1111 1111',
  },
  { key: 'pis', label: 'PIS/PASEP', patterns: ['000.00000.00-0'], inputMode: 'numeric', exemplo: '120.12345.67-8' },
  {
    key: 'processo',
    label: 'Processo (CNJ)',
    patterns: ['0000000-00.0000.0.00.0000'],
    inputMode: 'numeric',
    exemplo: '0001234-56.2026.8.07.0001',
  },
  {
    key: 'custom',
    label: 'Personalizada…',
    patterns: [],
    inputMode: 'text',
    exemplo: '',
  },
]

const PRONTAS_POR_KEY = new Map(MASCARAS_PRONTAS.map((m) => [m.key, m]))

/** Config de mascara guardada em `custom_field_config`. */
export type ConfigMascara = {
  /** Chave de `MASCARAS_PRONTAS` ou `custom`. Ausente = sem mascara. */
  mask?: unknown
  /** Padrao livre, usado apenas quando `mask` e `custom`. */
  maskPattern?: unknown
  /** O config do campo carrega outras chaves (opcoes, regras); nao atrapalham. */
  [k: string]: unknown
}

/** Uma mascara resolvida, pronta para uso pela UI. */
export type Mascara = { patterns: string[]; inputMode: 'numeric' | 'text' | 'tel'; exemplo: string }

/**
 * Le a mascara do config de um campo. Devolve `null` quando o campo nao tem
 * mascara — inclusive quando escolheram "Personalizada" e nao escreveram o
 * padrao, caso em que mascarar seria pior do que deixar o texto livre.
 */
export function mascaraDoCampo(config: ConfigMascara | null | undefined): Mascara | null {
  const key = typeof config?.mask === 'string' ? config.mask.trim() : ''
  if (!key) return null
  if (key === 'custom') {
    const pattern = typeof config?.maskPattern === 'string' ? config.maskPattern.trim() : ''
    if (!pattern) return null
    return { patterns: [pattern], inputMode: 'text', exemplo: exemploDoPadrao(pattern) }
  }
  const pronta = PRONTAS_POR_KEY.get(key)
  if (!pronta || pronta.patterns.length === 0) return null
  return { patterns: pronta.patterns, inputMode: pronta.inputMode ?? 'text', exemplo: pronta.exemplo }
}

/** Quantos caracteres o padrao consome de quem digita. */
export function capacidadeDoPadrao(pattern: string): number {
  let n = 0
  for (let i = 0; i < pattern.length; i++) {
    if (pattern[i] === '\\') {
      i++
      continue
    }
    if (MARCADORES.has(pattern[i]!)) n++
  }
  return n
}

/** O caractere serve para este marcador? */
function combina(marcador: string, ch: string): boolean {
  if (marcador === '0') return /[0-9]/.test(ch)
  if (marcador === 'A' || marcador === 'a') return /[\p{L}]/u.test(ch)
  return /[\p{L}0-9]/u.test(ch)
}

/** Normaliza o caractere aceito conforme o marcador (caixa alta/baixa). */
function normaliza(marcador: string, ch: string): string {
  if (marcador === 'A' || marcador === 'X') return ch.toLocaleUpperCase('pt-BR')
  if (marcador === 'a') return ch.toLocaleLowerCase('pt-BR')
  return ch
}

/**
 * Aplica UM padrao ao texto digitado.
 *
 * Os literais do padrao entram sozinhos, e os caracteres que nao servem para o
 * marcador da vez sao descartados: colar "123.456.789-00" num padrao de CPF
 * devolve o proprio CPF, e colar "12345678900" devolve o mesmo resultado.
 */
export function aplicarPadrao(valor: string, pattern: string): string {
  const bruto = [...valor]
  let out = ''
  let i = 0 // posicao no texto digitado
  for (let p = 0; p < pattern.length; p++) {
    let ch = pattern[p]!
    let literal = true
    if (ch === '\\') {
      p++
      ch = pattern[p] ?? ''
    } else if (MARCADORES.has(ch)) {
      literal = false
    }
    if (literal) {
      // so adianta escrever o literal se ainda ha o que digitar depois dele
      if (i >= bruto.length) break
      // quem digitou o proprio literal (colou um CPF formatado) nao repete
      if (bruto[i] === ch) i++
      out += ch
      continue
    }
    // pula o que nao serve para este marcador (letra num campo de digito, etc.)
    while (i < bruto.length && !combina(ch, bruto[i]!)) i++
    if (i >= bruto.length) break
    out += normaliza(ch, bruto[i]!)
    i++
  }
  return out
}

/**
 * Aplica a mascara escolhendo o padrao adequado ao tamanho do que foi digitado.
 * Com varios padroes (CPF ou CNPJ), vale o primeiro que ainda comporta tudo;
 * o maior deles e o limite, entao digitar alem simplesmente nao entra.
 */
export function aplicarMascara(valor: string, mascara: Mascara | null): string {
  if (!mascara || mascara.patterns.length === 0) return valor
  const uteis = [...valor].filter((ch) => /[\p{L}0-9]/u.test(ch)).length
  const escolhido =
    mascara.patterns.find((p) => uteis <= capacidadeDoPadrao(p)) ?? mascara.patterns[mascara.patterns.length - 1]!
  return aplicarPadrao(valor, escolhido)
}

/** Exemplo legivel de um padrao livre, para servir de placeholder. */
export function exemploDoPadrao(pattern: string): string {
  let out = ''
  for (let p = 0; p < pattern.length; p++) {
    const ch = pattern[p]!
    if (ch === '\\') {
      p++
      out += pattern[p] ?? ''
      continue
    }
    if (ch === '0') out += '0'
    else if (ch === 'A') out += 'A'
    else if (ch === 'a') out += 'a'
    else if (ch === 'S') out += 'S'
    else if (ch === 'X') out += 'X'
    else out += ch
  }
  return out
}

/**
 * O valor esta completo para a mascara? Serve para avisar quem digitou pela
 * metade — nao valida o conteudo (digito verificador de CPF, por exemplo).
 */
export function mascaraCompleta(valor: string, mascara: Mascara | null): boolean {
  if (!mascara || mascara.patterns.length === 0) return true
  if (!valor) return true
  const uteis = [...valor].filter((ch) => /[\p{L}0-9]/u.test(ch)).length
  return mascara.patterns.some((p) => capacidadeDoPadrao(p) === uteis)
}
